// frontend/src/App.js
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate as RouterNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Navigate } from './routing';
import { LANGUAGES, getLanguage, setLanguage } from './i18n';
import Seo from './components/Seo';

import Landing from './pages/Landing';
import HowItWorks from './pages/HowItWorks';
import Browse from './pages/Browse';
import ProductDetail from './pages/ProductDetail';
import Checkout from './pages/Checkout';
import MyRentals from './pages/MyRentals';
import RentalDetail from './pages/RentalDetail';
import Login from './pages/Login';
import Register from './pages/Register';
import AdminDashboard from './pages/admin/Dashboard';
import AdminProducts from './pages/admin/Products';
import AdminRentals from './pages/admin/Rentals';
import AdminDiscounts from './pages/admin/Discounts';
import AdminLockers from './pages/admin/Lockers';
import AdminCustomers from './pages/admin/Customers';
import AdminIssues from './pages/admin/Issues';
import AdminReviews from './pages/admin/Reviews';
import AdminSubscribers from './pages/admin/Subscribers';
import AdminDocuments from './pages/admin/Documents';
import AdminActivity from './pages/admin/Activity';
import AdminAffiliates from './pages/admin/Affiliates';
import AdminLicenses from './pages/admin/Licenses';
import Partner from './pages/Partner';
import Partners from './pages/Partners';
import PartnerTerms from './pages/PartnerTerms';
import LicenseTerms from './pages/LicenseTerms';
import ReferralLanding from './pages/ReferralLanding';
import Profile from './pages/Profile';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ConfirmEmail from './pages/ConfirmEmail';
import DeleteAccount from './pages/DeleteAccount';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import WithdrawalForm from './pages/WithdrawalForm';
import Contact from './pages/Contact';
import Navbar from './components/Navbar';
import LegalFooter from './components/LegalFooter';
import theme from './theme';
import { captureReferralFromUrl } from './utils/referral';

// Lazy so that none of it lands in the main bundle — the same bundle is what
// the Android app ships. Declared after the imports rather than among them:
// a `const` in the middle of the import list is hoisting-sensitive and is what
// `import/first` is warning about.
const Styleguide = React.lazy(() => import('./pages/Styleguide'));

// Returning null here painted a blank white page for as long as /auth/me took —
// up to the 15s axios timeout on a slow backend, which reads as a crash rather
// than as loading.
function RouteLoading() {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: theme.font.display, fontSize: 24, color: theme.gold }}>
      …
    </div>
  );
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <RouteLoading />;
  return user ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <RouteLoading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

// The site, written once, without a language in it.
//
// Paths here are relative because this whole tree is mounted under a `/:lang/*`
// splat — 'browse' resolves against /el or /en. Nothing inside needs to know
// which: components link with `<Link to="/browse">` and src/routing.js puts the
// prefix back on.
function LocalizedRoutes() {
  return (
    <Routes>
      <Route path="" element={<Landing />} />
      <Route path="how-it-works" element={<><Navbar /><HowItWorks /></>} />
      <Route path="browse" element={<><Navbar /><Browse /></>} />
      <Route path="product/:id" element={<><Navbar /><ProductDetail /></>} />
      <Route path="login" element={<Login />} />
      <Route path="register" element={<Register />} />
      <Route path="forgot-password" element={<ForgotPassword />} />
      <Route path="reset-password" element={<ResetPassword />} />
      <Route path="checkout/:rentalId" element={
        <PrivateRoute><Navbar /><Checkout /></PrivateRoute>
      } />
      <Route path="my-rentals" element={
        <PrivateRoute><Navbar /><MyRentals /></PrivateRoute>
      } />
      <Route path="profile" element={
        <PrivateRoute><Navbar /><Profile /></PrivateRoute>
      } />
      <Route path="rental/:id" element={
        <PrivateRoute><Navbar /><RentalDetail /></PrivateRoute>
      } />
      <Route path="admin" element={
        <AdminRoute><AdminDashboard /></AdminRoute>
      } />
      <Route path="admin/products" element={
        <AdminRoute><AdminProducts /></AdminRoute>
      } />
      <Route path="admin/rentals" element={
        <AdminRoute><AdminRentals /></AdminRoute>
      } />
      <Route path="admin/discounts" element={
        <AdminRoute><AdminDiscounts /></AdminRoute>
      } />
      <Route path="admin/lockers" element={
        <AdminRoute><AdminLockers /></AdminRoute>
      } />
      <Route path="admin/customers" element={
        <AdminRoute><AdminCustomers /></AdminRoute>
      } />
      <Route path="admin/issues" element={
        <AdminRoute><AdminIssues /></AdminRoute>
      } />
      <Route path="admin/reviews" element={
        <AdminRoute><AdminReviews /></AdminRoute>
      } />
      <Route path="admin/subscribers" element={
        <AdminRoute><AdminSubscribers /></AdminRoute>
      } />
      <Route path="admin/documents" element={
        <AdminRoute><AdminDocuments /></AdminRoute>
      } />
      {/* Reads ?entity_id= itself, so /admin/activity?entity_id=<rentalId>
          is a working deep link without a route of its own. */}
      <Route path="admin/activity" element={
        <AdminRoute><AdminActivity /></AdminRoute>
      } />
      <Route path="admin/affiliates" element={
        <AdminRoute><AdminAffiliates /></AdminRoute>
      } />
      <Route path="admin/licenses" element={
        <AdminRoute><AdminLicenses /></AdminRoute>
      } />
      {/* A partner's own dashboard. Behind PrivateRoute rather than a role
          guard: partnership is a row in the database, not a claim in the token,
          so the SERVER decides — the page asks and shows an invitation to the
          programme if the answer is no. A role check here would have to be
          re-issued into every token the moment somebody became a partner. */}
      <Route path="partner" element={
        <PrivateRoute><Partner /></PrivateRoute>
      } />
      {/* Renders its own Navbar, like Landing — no wrapper here or the
          page gets two. */}
      <Route path="partners" element={<Partners />} />
      <Route path="partner-terms" element={<PartnerTerms />} />
      <Route path="license-terms" element={<LicenseTerms />} />
      <Route path="confirm-email" element={<ConfirmEmail />} />
      <Route path="verify-email" element={<ConfirmEmail mode="verify" />} />
      <Route path="delete-account" element={<DeleteAccount />} />
      <Route path="privacy" element={<PrivacyPolicy />} />
      <Route path="terms" element={<Terms />} />
      {/* Annex I(B) model withdrawal form — linked from the Terms, the
          checkout and the contract-confirmation email. */}
      <Route path="withdrawal-form" element={<WithdrawalForm />} />
      <Route path="contact" element={<Contact />} />
      {/* Every design token, rendered. Lazy so that none of it lands in the
          main bundle — the same bundle is what the Android app ships. */}
      <Route path="styleguide" element={
        <React.Suspense fallback={<RouteLoading />}><Styleguide /></React.Suspense>
      } />
      {/* An unknown path under a valid language. Home, rather than a blank
          screen — and `replace` so Back does not return to the dead URL. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Keeps the rendered language equal to the language the URL claims.
//
// On a cold load i18n has already read the URL itself (see detect() in i18n.js)
// so this is a no-op; it earns its place on in-app navigation between /el/… and
// /en/…, e.g. following a link from an email written in the other language.
function LocaleShell({ lang }) {
  React.useEffect(() => {
    if (getLanguage() !== lang) setLanguage(lang);
  }, [lang]);
  return <><Seo /><LocalizedRoutes /><GlobalLegalFooter /></>;
}

// Who we are, and where the legal texts are, at the bottom of every page.
//
// Mounted here rather than added to each page for one reason: a page that has to
// remember is a page that will forget. Browse, the product page, checkout, My
// Rentals, the profile and every auth screen shipped with no footer at all,
// while Art. 5 of the E-Commerce Directive asks for the trader's identity to be
// permanently accessible and CRD Art. 6(1) wants it reachable before the
// consumer is bound — which is precisely where those pages sit. Rendering it
// from the shell means the next page added is covered on the day it is written.
//
// Two exclusions, both deliberate:
//   · the landing page has its own, larger footer with the same links and the
//     same identity block, and two footers stacked is not better than one;
//   · /admin/* is a staff tool. Nobody there is a consumer being informed, and a
//     consumer-rights footer under an operations table is noise.
function GlobalLegalFooter() {
  const { pathname } = useLocation();
  // The path still carries the /el or /en prefix at this level.
  const rest = pathname.replace(/^\/[a-z]{2}(?=\/|$)/, '') || '/';
  if (rest === '/' || rest.startsWith('/admin') || rest.startsWith('/styleguide')) return null;
  return <LegalFooter />;
}

// Everything that arrives without a language — '/', deep links in the backend's
// emails ('/reset-password?token=…'), old bookmarks — is sent to the same path
// under the reader's language, so there is exactly one URL per page per
// language and never two that serve the same thing.
//
// The query string and fragment ride along, which is not cosmetic: those email
// links carry the one-time token in the query, and dropping it would turn every
// password reset into a dead end.
function RedirectToLocale() {
  const { pathname, search, hash } = useLocation();
  const path = pathname === '/' ? '' : pathname;
  return <RouterNavigate to={`/${getLanguage()}${path}${search}${hash}`} replace />;
}

export default function App() {
  // Once, before anything renders. A ?ref= on ANY page — not only the landing —
  // is remembered and stripped from the address bar, so a link a salon shared to
  // a specific product still credits them, and the code does not survive into
  // whatever the visitor copies out of the address bar next.
  React.useEffect(() => { captureReferralFromUrl(); }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" toastOptions={{
          style: { background: theme.ink, color: theme.creamDeep, borderRadius: '12px' },
          success: { iconTheme: { primary: theme.gold, secondary: theme.ink } }
        }} />
        <Routes>
          {/* One mount per language rather than a `/:lang/*` pattern: with the
              pattern, /browse would match with lang="browse" and the fallback
              below would never be reached. */}
          {Object.keys(LANGUAGES).map((lng) => (
            <Route key={lng} path={`/${lng}/*`} element={<LocaleShell lang={lng} />} />
          ))}
          {/* Partner links and QR codes. Outside the language prefix on
              purpose: this URL gets printed onto a sticker, and a printed thing
              cannot be re-issued when the reader turns out to speak the other
              language. */}
          <Route path="/r/:code" element={<ReferralLanding />} />
          <Route path="*" element={<RedirectToLocale />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
